import { useParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader, Section } from '@/components/layout/AppShell'
import { Card, StatCard } from '@/components/ui/Card'
import { Button, IconButton } from '@/components/ui/Button'
import { DetailPageSkeleton } from '@/components/ui/Skeleton'
import { Pagination } from '@/components/ui/Pagination'
import { ProjectStatusBadge } from '@/components/shared/StatusBadge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { CompactAmount } from '@/components/shared/AmountDisplay'
import { useAgency, useAgencyTenders } from '@/hooks/useAgencies'
import { PROVINCE_NAMES } from '@/lib/constants'
import { useState } from 'react'

export function AgencyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tenderPage, setTenderPage] = useState(1)

  const { data: agency, isLoading: agencyLoading } = useAgency(id!)
  const { data: tendersData, isLoading: tendersLoading } = useAgencyTenders(id!, tenderPage)

  if (agencyLoading) {
    return <DetailPageSkeleton />
  }

  if (!agency) {
    return (
      <Card className="p-8 text-center">
        <Icon icon="solar:buildings-line-duotone" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="font-display text-xl font-semibold text-slate-900">ไม่พบหน่วยงาน</h2>
        <p className="text-slate-500 mt-2">หน่วยงานนี้อาจถูกลบหรือไม่มีอยู่</p>
        <Link to="/agencies">
          <Button className="mt-4">
            <Icon icon="solar:arrow-left-line-duotone" className="w-4 h-4 mr-2" />
            กลับไปหน้าหน่วยงาน
          </Button>
        </Link>
      </Card>
    )
  }

  const breadcrumbs = [
    { label: 'หน่วยงาน', href: '/agencies' },
    { label: agency.name?.slice(0, 30) + '...' || 'หน่วยงาน' },
  ]

  return (
    <div>
      <PageHeader
        title=""
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              icon="solar:bookmark-line-duotone"
              label="บันทึก"
              variant="ghost"
            />
            <IconButton
              icon="solar:share-line-duotone"
              label="แชร์"
              variant="ghost"
            />
          </div>
        }
      />

      {/* Agency header */}
      <Card className="mb-6">
        <div className="flex items-start gap-6">
          {/* Icon */}
          <div className="w-20 h-20 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center">
            <Icon icon="solar:buildings-line-duotone" className="w-10 h-10" />
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {agency.name}
            </h1>

            {agency.ministry && (
              <div className="flex items-center gap-2 mt-2 text-slate-600">
                <Icon icon="solar:building-line-duotone" className="w-5 h-5 text-slate-400" />
                <span>{agency.ministry}</span>
              </div>
            )}

            {agency.province_code && (
              <div className="flex items-center gap-2 mt-2 text-slate-600">
                <Icon icon="solar:map-point-line-duotone" className="w-5 h-5 text-slate-400" />
                <span>{PROVINCE_NAMES[agency.province_code] || agency.province_code}</span>
              </div>
            )}

            {agency.dept_code && (
              <div className="flex items-center gap-2 mt-2 text-slate-500">
                <Icon icon="solar:hashtag-line-duotone" className="w-5 h-5 text-slate-400" />
                <span className="font-mono text-sm">รหัส: {agency.dept_code}</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      {tendersData && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="ประกาศทั้งหมด"
            value={tendersData.totalItems}
            icon={<Icon icon="solar:document-text-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="ประกาศที่เปิดอยู่"
            value={tendersData.items.filter((t: any) => ['announced', 'bidding'].includes(t.project_status)).length}
            icon={<Icon icon="solar:play-circle-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="เสร็จสิ้น"
            value={tendersData.items.filter((t: any) => t.project_status === 'completed').length}
            icon={<Icon icon="solar:check-circle-line-duotone" className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Tenders */}
      <Section title="ประกาศของหน่วยงาน" description="โครงการจัดซื้อจัดจ้างล่าสุดจากหน่วยงานนี้">
        {tendersLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-4 w-3/4 bg-slate-200 rounded" />
                <div className="h-3 w-1/2 bg-slate-200 rounded mt-2" />
              </Card>
            ))}
          </div>
        ) : tendersData?.items.length === 0 ? (
          <Card className="p-8 text-center">
            <Icon icon="solar:document-text-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900">ยังไม่มีประกาศ</h3>
            <p className="text-sm text-slate-500 mt-1">
              ประกาศของหน่วยงานนี้จะแสดงที่นี่
            </p>
          </Card>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {tendersData?.items.map((tender: any) => (
                <Link key={tender.id} to={`/tenders/${tender.id}`}>
                  <Card hover>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 line-clamp-2 hover:text-blue-600">
                          {tender.project_name || 'ประกาศไม่มีชื่อ'}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <ProjectStatusBadge status={tender.project_status} />
                          {tender.published_at && (
                            <span className="text-sm text-slate-500">
                              <DateDisplay date={tender.published_at} format="relative" />
                            </span>
                          )}
                        </div>
                      </div>
                      {tender.sum_price_agree && (
                        <CompactAmount satang={tender.sum_price_agree} className="flex-shrink-0" />
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {tendersData && tendersData.totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={tenderPage}
                  totalPages={tendersData.totalPages}
                  onPageChange={setTenderPage}
                />
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  )
}
